import React from 'react';
import { Play, Pause } from 'lucide-react';
import { WaveformVisualizer } from './WaveformVisualizer';
interface VoiceCardProps {
  name: string;
  description: string;
  tags: string[];
  color: string;
}
function VoiceCard({
  name,
  description,
  tags,
  color
}: VoiceCardProps) {
  return <div className="group relative bg-[#13131F] border border-white/5 rounded-xl p-6 hover:border-[#00F0FF]/50 transition-all duration-300 hover:shadow-[0_0_30px_rgba(0,240,255,0.1)]" data-id="element-160">
      <div className="flex justify-between items-start mb-4" data-id="element-161">
        <div data-id="element-162">
          <h3 className="text-xl font-bold text-white font-mono" data-id="element-163">{name}</h3>
          <div className="flex gap-2 mt-2" data-id="element-164">
            {tags.map(tag => <span key={tag} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-white/5 text-gray-400 border border-white/10" data-id="element-165">
                {tag}
              </span>)}
          </div>
        </div>
        <button className="w-10 h-10 rounded-full bg-[#00F0FF] text-black flex items-center justify-center hover:bg-white transition-colors" aria-label={`Play ${name} sample`} data-id="element-166">
          <Play size={18} fill="currentColor" data-id="element-167" />
        </button>
      </div>

      <p className="text-gray-400 text-sm mb-6 min-h-[40px]" data-id="element-168">{description}</p>

      <div className="bg-black/40 rounded-lg p-3 border border-white/5 group-hover:border-[#00F0FF]/20 transition-colors" data-id="element-169">
        <WaveformVisualizer height={30} bars={25} color={color} animated={false} className="group-hover:animate-pulse" data-id="element-170" />
      </div>
    </div>;
}
export function VoiceShowcase() {
  const voices = [{
    name: 'Zephyrine',
    description: 'Ethereal, versatile female voice perfect for fantasy narration and meditation.',
    tags: ['Female', 'Soft', 'Ethereal'],
    color: '#00F0FF'
  }, {
    name: 'Caelum',
    description: 'Deep, resonant male voice with gravitas. Ideal for trailers and documentaries.',
    tags: ['Male', 'Deep', 'Authoritative'],
    color: '#FF00AA'
  }, {
    name: 'Thessaly',
    description: 'Warm, engaging narrator with high emotional intelligence and clarity.',
    tags: ['Female', 'Warm', 'Narrative'],
    color: '#8B5CF6'
  }, {
    name: 'Ondine',
    description: 'Youthful, energetic tone for commercials, gaming, and dynamic content.',
    tags: ['Female', 'Young', 'Energetic'],
    color: '#00F0FF'
  }, {
    name: 'Lysander',
    description: 'Professional, crisp articulation for corporate training and news reading.',
    tags: ['Male', 'Professional', 'Crisp'],
    color: '#FF00AA'
  }];
  return <section id="voices" className="py-24 bg-[#0A0A0F] relative" data-id="element-171">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-172">
        <div className="text-center mb-16" data-id="element-173">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-174">
            Meet Our Voice Ensemble
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto" data-id="element-175">
            Powered by our proprietary{' '}
            <span className="text-[#00F0FF] font-mono" data-id="element-176">Resonance Lab™</span>{' '}
            technology, each voice model captures the subtle nuances of human
            expression.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-177">
          {voices.map(voice => <VoiceCard key={voice.name} {...voice} data-id="element-178" />)}

          {/* Call to Action Card */}
          <div className="flex flex-col items-center justify-center p-6 border border-dashed border-white/20 rounded-xl bg-white/5 hover:bg-white/10 transition-colors cursor-pointer group" data-id="element-179">
            <div className="w-12 h-12 rounded-full border border-[#00F0FF] flex items-center justify-center text-[#00F0FF] mb-4 group-hover:scale-110 transition-transform" data-id="element-180">
              <span className="text-2xl" data-id="element-181">+</span>
            </div>
            <h3 className="text-lg font-bold text-white mb-2" data-id="element-182">Custom Voice</h3>
            <p className="text-gray-400 text-sm text-center" data-id="element-183">
              Clone your own voice or create a custom brand voice.
            </p>
          </div>
        </div>
      </div>
    </section>;
}